import { Dropdown } from 'Common/Components/SideBarDropdown';
import useIsMobile from 'Common/useIsMobile';
import { getAllowedMenu } from 'helpers/routeAccess';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { appDispatch } from 'slices';
import { LoginHook } from 'slices/auth/login/hook';
import { LayoutHook } from 'slices/layouts/hook';
import { changeLayoutSidebarVisible } from 'slices/thunk';

const VerticalLayout = () => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const dispatch = appDispatch();
  const { account, isAdmin } = LoginHook();
  const { activePath, layoutSidebarSizeType } = LayoutHook();
  const [tooltip, setTooltip] = React.useState<{ label: string; top: number; left: number } | null>(
    null,
  );

  const filteredMenu = useMemo(() => getAllowedMenu(account?.permissions, isAdmin), [account, isAdmin]);

  const closeSidebar = () => {
    dispatch(changeLayoutSidebarVisible(false));
  };

  const showTooltip = (element: HTMLElement, label: string) => {
    if (layoutSidebarSizeType !== 'sm') return;

    const rect = element.getBoundingClientRect();
    setTooltip({
      label,
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
  };

  const hideTooltip = () => {
    setTooltip(null);
  };

  const isItemActive = (item: any): boolean => {
    if (!activePath) return false;
    if (item.link && item.link === activePath) return true;
    if (item.subItems && item.subItems.length) {
      return item.subItems.some((sub: any) => {
        if (sub.link && sub.link === activePath) return true;
        if (sub.subItems) return sub.subItems.some((s: any) => s.link === activePath);
        return false;
      });
    }
    return false;
  };

  return (
    <>
      {(filteredMenu || []).map((item: any, idx: number) => {
        if (item?.mobileOnly && !isMobile) return null;

        if (item.isTitle) {
          if (layoutSidebarSizeType === 'sm') return null;
          return (
            <li key={idx} className="mt-3 px-4 py-1 uppercase text-xs font-medium">
              <span>{t(item.label)}</span>
            </li>
          );
        }

        if (item.subItems && item.subItems.length) {
          const active = isItemActive(item);
          return (
            <Dropdown key={idx} className="relative" id={item.id}>
              <Dropdown.Trigger
                type="a"
                className={`dropdown-button group/menu-link relative flex items-center ${layoutSidebarSizeType === 'sm' ? 'justify-center px-4' : 'px-3'} py-2 text-16 md:text-base transition-all duration-75 ease-linear rounded-md text-vertical-menu-item hover:text-vertical-menu-item-hover hover:bg-vertical-menu-item-bg-hover ${active ? 'active show' : ''}`}
                title={layoutSidebarSizeType === 'sm' ? t(item.label) : undefined}
                onMouseEnter={(event) => showTooltip(event.currentTarget, t(item.label))}
                onMouseLeave={hideTooltip}
                onFocus={(event) => showTooltip(event.currentTarget, t(item.label))}
                onBlur={hideTooltip}
              >
                <span className="min-w-[1.75rem] inline-block">
                  {item.icon && React.cloneElement(item.icon, { className: 'h-4' })}
                </span>
                {layoutSidebarSizeType !== 'sm' && <span className="ml-2">{t(item.label)}</span>}
              </Dropdown.Trigger>
              {layoutSidebarSizeType !== 'sm' && (
                <Dropdown.Content className={`dropdown-content ${active ? '' : 'hidden'}`}>
                  <ul>
                    {item.subItems.map((sub: any, sidx: number) => (
                      <li key={sidx}>
                        <Link
                          to={sub.link}
                          onClick={closeSidebar}
                          className={`block px-4 py-2 text-16 md:text-base transition-all duration-75 ease-linear hover:text-vertical-menu-sub-item-hover ${sub.link === activePath ? 'active' : ''}`}
                        >
                          {t(sub.label)}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </Dropdown.Content>
              )}
            </Dropdown>
          );
        }

        const active = isItemActive(item);
        return (
          <li key={idx}>
            <Link
              to={item.link}
              onClick={closeSidebar}
              title={layoutSidebarSizeType === 'sm' ? t(item.label) : undefined}
              onMouseEnter={(event) => showTooltip(event.currentTarget, t(item.label))}
              onMouseLeave={hideTooltip}
              onFocus={(event) => showTooltip(event.currentTarget, t(item.label))}
              onBlur={hideTooltip}
              className={`group/menu-link relative flex items-center ${layoutSidebarSizeType === 'sm' ? 'justify-center px-4' : ''} px-4 py-2 menu text-16 md:text-base transition-all duration-75 ease-linear text-vertical-menu-item hover:text-vertical-menu-item-hover hover:bg-vertical-menu-item-bg-hover ${active ? 'active' : ''}`}
            >
              <span
                className={`min-w-[1.75rem] inline-block ${layoutSidebarSizeType === 'sm' ? 'inline-flex justify-center' : ''}`}
              >
                {item.icon && React.cloneElement(item.icon, { className: 'h-4' })}
              </span>
              {layoutSidebarSizeType !== 'sm' && <span className="ml-2">{t(item.label)}</span>}
            </Link>
          </li>
        );
      })}
      {tooltip && (
        <span
          className="pointer-events-none fixed z-[1100] whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-sm text-white shadow-md"
          style={{ top: tooltip.top, left: tooltip.left, transform: 'translateY(-50%)' }}
        >
          {tooltip.label}
        </span>
      )}
    </>
  );
};

export default VerticalLayout;
